import { useEffect, useState } from "react";
import { Plus, Info } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { Stat, PageHeader, Loading, Money, ProgressBar, Modal, Field, ErrBox, Table, Empty } from "../../components/common";

export default function TaxVault() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "reserve", amount: "", note: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/vault/summary").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await API.post("/vault/entries", form);
      toast.success("Vault entry recorded");
      setOpen(false); setForm({ type: "reserve", amount: "", note: "" });
      load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  if (!data) return <Loading label="Loading Tax Vault" />;
  const tone = data.funded_pct >= 100 ? "green" : data.funded_pct >= 80 ? "yellow" : "red";

  return (
    <div data-testid="tax-vault-page">
      <PageHeader title="Tax Vault" sub="Your tax reserve ledger — what should be set aside vs. what is."
        right={<button className="btn-navy" onClick={() => setOpen(true)} data-testid="vault-add-entry"><Plus className="h-4 w-4" /> Record Reserve</button>} />

      <div className="mb-5 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="vault-ledger-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{data.ledger_only_notice} Automatic sweeps require a connected account, your explicit authorization, and an active payment provider — and will only be enabled when admin settings permit it.</span>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Outstanding" cents={data.outstanding_cents ?? data.estimated_tax_cents} tone="red" testid="vault-outstanding" />
        <Stat label="Tax Required (current)" cents={data.estimated_tax_cents} tone="gold" testid="vault-required" />
        <Stat label="Tax Reserved" cents={data.reserved_cents} tone="green" testid="vault-reserved" />
        <Stat label="Shortfall" cents={data.shortfall_cents} tone={data.shortfall_cents > 0 ? "red" : "green"} testid="vault-shortfall" />
        <Stat label="Next Payment" value={data.next_payment ? fmtDate(data.next_payment.due_date) : "None scheduled"}
          tone="blue" testid="vault-next-payment" />
      </div>

      <div className="card-gov mt-6 border-l-4 border-l-rose-500 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-bold text-navy">Tax Vault Funded</h3>
            <div className="mt-1 text-sm text-slate-500">Total outstanding tax obligation: <Money cents={data.outstanding_cents ?? data.estimated_tax_cents} exact className="text-lg font-bold text-rose-600" /></div>
          </div>
          <span className={`money text-2xl font-bold ${tone === "green" ? "text-emerald-600" : tone === "yellow" ? "text-amber-600" : "text-rose-600"}`} data-testid="vault-funded-pct">{data.funded_pct}%</span>
        </div>
        <div className="mt-3"><ProgressBar pct={data.funded_pct} tone={tone} /></div>
        <div className="mt-2 flex justify-between text-xs text-slate-500">
          <span><Money cents={data.reserved_cents} /> reserved</span>
          <span><Money cents={data.outstanding_cents ?? data.estimated_tax_cents} /> total outstanding</span>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-3 font-bold text-navy">Reserve Ledger</h3>
        {data.entries.length === 0 ? (
          <Empty title="No reserve entries yet" msg="Record your first tax reserve to start tracking funding." />
        ) : (
          <Table headers={["Date", "Type", "Amount", "Note", "Recorded By"]} testid="vault-entries-table">
            {data.entries.map((e) => (
              <tr key={e.id} className="border-b border-slate-100">
                <td className="px-4 py-3">{fmtDate(e.date)}</td>
                <td className="px-4 py-3 capitalize">{e.type}</td>
                <td className={`px-4 py-3 money font-semibold ${e.amount_cents < 0 ? "text-rose-600" : "text-emerald-700"}`}><Money cents={e.amount_cents} exact /></td>
                <td className="px-4 py-3 text-slate-500">{e.note || "—"}</td>
                <td className="px-4 py-3 text-slate-400">{e.created_by}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Record Tax Vault Entry">
        <form onSubmit={submit} className="space-y-4" data-testid="vault-entry-form">
          <Field label="Entry Type">
            <select className="input-gov" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="vault-entry-type">
              <option value="reserve">Reserve (add funds)</option>
              <option value="withdraw">Withdraw (use reserved funds)</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </Field>
          <Field label="Amount ($)"><input className="input-gov money" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="vault-entry-amount" /></Field>
          <Field label="Note"><input className="input-gov" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="vault-entry-note" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="vault-entry-submit">{busy ? "Saving…" : "Save Entry"}</button>
        </form>
      </Modal>
    </div>
  );
}
